import { TranslationKeys, translate } from '../../../i18n';
import { VenueAgentDto } from '../../../models/court-recording/dto/venue-agent.dto';
import { CameraSnapshotSubject } from '../hooks/useGetCameraSnapshotUrl';
import {
  CameraAssignmentRow,
  STALE_LAST_SEEN_THRESHOLD_MS,
  cameraAssignmentRows,
} from './venue-agent.utils';

/**
 * The venue-facing camera setup page (venue-setup epic, stage 3c).
 *
 * Everything an installer at the venue sees is derived HERE, as pure
 * functions over the agent DTO, and tested here — the page is a renderer.
 * Same rule as `venue-agent.utils.ts` (D10): prose lives in pure code where a
 * truth table can pin it, never in JSX a Chakra mock would swallow.
 *
 * The audience is the person who plugged the box in: no MACs, no court ids,
 * no "heartbeat". Every sentence names a thing they can touch (a cable, a
 * switch port, a picture) or a thing they can wait for (a minute).
 */

type CameraSetupKey = TranslationKeys extends infer K
  ? K extends `court_recording.camera_setup.${infer Rest}`
    ? Rest
    : never
  : never;

export const cst = (
  key: CameraSetupKey,
  params?: Record<string, string | number>,
): string =>
  translate(`court_recording.camera_setup.${key}` as TranslationKeys, params);

// ---------------------------------------------------------------------------
// The box
// ---------------------------------------------------------------------------

export type BoxStatus = 'online' | 'offline' | 'never-seen' | 'revoked';

/** Online = a heartbeat inside the API's own offline threshold. */
export function boxStatus(agent: VenueAgentDto, now: number = Date.now()): BoxStatus {
  if (agent.revokedAt) return 'revoked';
  if (!agent.lastSeenAt) return 'never-seen';
  return now - new Date(agent.lastSeenAt).getTime() > STALE_LAST_SEEN_THRESHOLD_MS
    ? 'offline'
    : 'online';
}

// ---------------------------------------------------------------------------
// Pictures
// ---------------------------------------------------------------------------

/**
 * How long after "take new photos" the page keeps saying "taking…" before it
 * gives up on that request. The box captures within one heartbeat (~60 s)
 * plus one capture per camera (≤ 15 s each); the API echoes the request for
 * 10 minutes. Three minutes is the point where "still waiting" stops being
 * informative and the installer should press again or check the box.
 */
export const SNAPSHOT_REQUEST_PATIENCE_MS = 3 * 60 * 1000;

/**
 * A request stamped slightly AFTER the page's own clock still counts as
 * live: the API stamps it with its clock, the page re-reads its `now` every
 * 10 s, and the installer who just pressed the button must see "taking…"
 * immediately rather than on the next tick. Anything further ahead than this
 * is clock skew worth ignoring, not a request worth waiting on.
 */
export const SNAPSHOT_REQUEST_FUTURE_TOLERANCE_MS = 60 * 1000;

export type PictureState =
  | { kind: 'none' }
  | { kind: 'waiting' }
  | { kind: 'fresh'; takenAt: string };

/**
 * `waiting` wins while a recent request is newer than the last picture —
 * the honest state between the button press and the box's next heartbeat.
 * After `SNAPSHOT_REQUEST_PATIENCE_MS` the request is treated as spent and
 * the last picture (or its absence) is shown as is.
 */
export function pictureState(
  lastSnapshotAt: string | undefined,
  snapshotRequestedAt: string | undefined,
  now: number = Date.now(),
): PictureState {
  const requestedMs = snapshotRequestedAt ? new Date(snapshotRequestedAt).getTime() : NaN;
  const takenMs = lastSnapshotAt ? new Date(lastSnapshotAt).getTime() : NaN;
  const requestLive =
    Number.isFinite(requestedMs) &&
    now - requestedMs > -SNAPSHOT_REQUEST_FUTURE_TOLERANCE_MS &&
    now - requestedMs < SNAPSHOT_REQUEST_PATIENCE_MS;
  if (requestLive && (!Number.isFinite(takenMs) || takenMs < requestedMs)) {
    return { kind: 'waiting' };
  }
  if (Number.isFinite(takenMs)) return { kind: 'fresh', takenAt: lastSnapshotAt as string };
  return { kind: 'none' };
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export interface CameraSetupCard {
  row: CameraAssignmentRow;
  /** 1-based, in the order the box reports its cameras. What the installer calls it. */
  index: number;
  /** Stable key for React and for the confirmation store: the MAC when known. */
  key: string;
  picture: PictureState;
  subject: CameraSnapshotSubject | null;
  /** The court this card currently stands for: the admin assignment, else the box's own config. */
  courtId: number | null;
  /** Whether a confirmation would mean anything right now. */
  canConfirm: boolean;
  confirmed: boolean;
}

/** `{ cardKey: courtId }` — what the installer ticked, valid only for that court. */
export type Confirmations = Record<string, number>;

export function cameraSetupCards(
  agent: VenueAgentDto,
  confirmations: Confirmations,
  now: number = Date.now(),
): CameraSetupCard[] {
  const rows = cameraAssignmentRows(agent);
  return rows.map((row, i) => {
    const camera = (agent.cameras ?? []).find((c) =>
      row.mac ? c.mac === row.mac : c.id === String(row.reportedCourtId),
    );
    const picture = pictureState(camera?.lastSnapshotAt, agent.snapshotRequestedAt, now);
    const courtId = row.assignedCourtId ?? row.reportedCourtId;
    const subject: CameraSnapshotSubject | null = row.mac
      ? { mac: row.mac }
      : row.reportedCourtId !== null
        ? { courtId: row.reportedCourtId }
        : null;
    // A tick means "I looked at THIS picture and it shows THIS court": it
    // needs a picture, a camera that is actually streaming, and a mapping
    // the box has applied (pending = the picture may be about to move).
    const canConfirm =
      row.connected === true &&
      (row.state === 'applied' || row.state === 'unassigned') &&
      picture.kind === 'fresh' &&
      courtId !== null;
    const key = row.mac ?? row.key;
    return {
      row,
      index: i + 1,
      key,
      picture,
      subject,
      courtId,
      canConfirm,
      confirmed: canConfirm && confirmations[key] === courtId,
    };
  });
}

/**
 * The full `{ MAC: courtId }` map to PATCH when one card changes. The API
 * replaces rather than merges, so every assignable camera is restated at its
 * current court — otherwise saving camera 2 would silently clear camera 1.
 */
export function assignmentDraft(
  cards: CameraSetupCard[],
  override?: { key: string; courtId: number },
): Record<string, number> {
  const draft: Record<string, number> = {};
  for (const card of cards) {
    if (!card.row.mac) continue;
    const courtId = override && override.key === card.key ? override.courtId : card.courtId;
    if (courtId !== null) draft[card.row.mac] = courtId;
  }
  return draft;
}

/** The court chosen for two cameras in a draft, if any. */
export function duplicateCourtIn(draft: Record<string, number>): number | null {
  const seen = new Set<number>();
  for (const courtId of Object.values(draft)) {
    if (seen.has(courtId)) return courtId;
    seen.add(courtId);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Activation
// ---------------------------------------------------------------------------

export type SetupBlocker =
  | 'box_offline'
  | 'no_cameras'
  | 'camera_disconnected'
  | 'camera_not_ready'
  | 'duplicate_court'
  | 'pending_apply'
  | 'unconfirmed';

/**
 * Why "switch recording on" is not yet allowed, in the order the installer
 * should fix them (the box before the cameras, the cameras before the
 * mapping, the mapping before the ticks). Empty = ready. This is the
 * dashboard's own gate: the API lets an admin activate anything (operator
 * judgment, decision D1 of the pre-provisioning epic), and the operator view
 * keeps that power; this page is for someone who should not have to judge.
 */
export function setupBlockers(
  agent: VenueAgentDto,
  cards: CameraSetupCard[],
  now: number = Date.now(),
): SetupBlocker[] {
  const blockers: SetupBlocker[] = [];
  const status = boxStatus(agent, now);
  if (status !== 'online') blockers.push('box_offline');
  if (cards.length === 0) {
    blockers.push('no_cameras');
    return blockers;
  }
  if (cards.some((c) => c.row.state === 'not-reporting' || c.row.connected === false)) {
    blockers.push('camera_disconnected');
  }
  if (cards.some((c) => c.row.state === 'no-mac' && c.row.connected !== false)) {
    blockers.push('camera_not_ready');
  }
  if (duplicateCourtIn(assignmentDraft(cards)) !== null) blockers.push('duplicate_court');
  if (cards.some((c) => c.row.state === 'pending')) blockers.push('pending_apply');
  if (cards.some((c) => !c.confirmed)) blockers.push('unconfirmed');
  return blockers;
}

export function describeBlocker(blocker: SetupBlocker): string {
  switch (blocker) {
    case 'box_offline':
      return cst('blocker_box_offline');
    case 'no_cameras':
      return cst('blocker_no_cameras');
    case 'camera_disconnected':
      return cst('blocker_camera_disconnected');
    case 'camera_not_ready':
      return cst('blocker_camera_not_ready');
    case 'duplicate_court':
      return cst('blocker_duplicate_court');
    case 'pending_apply':
      return cst('blocker_pending_apply');
    case 'unconfirmed':
      return cst('blocker_unconfirmed');
  }
}

/** One plain sentence per camera about its state, for the card. */
export function describeCameraState(card: CameraSetupCard): string {
  switch (card.row.state) {
    case 'not-reporting':
      return cst('state_not_reporting');
    case 'no-mac':
      return card.row.connected === false ? cst('state_disconnected') : cst('state_no_mac');
    case 'pending':
      return cst('state_pending');
    default:
      return card.row.connected === false ? cst('state_disconnected') : cst('state_connected');
  }
}

export function describePicture(picture: PictureState, now: number = Date.now()): string {
  switch (picture.kind) {
    case 'none':
      return cst('picture_none');
    case 'waiting':
      return cst('picture_waiting');
    case 'fresh':
      return cst('picture_taken', { ago: relativeAgo(picture.takenAt, now) });
  }
}

/** "2 min ago"-style, in the installer's language; coarse on purpose. */
export function relativeAgo(iso: string, now: number = Date.now()): string {
  const seconds = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return cst('ago_seconds', { count: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return cst('ago_minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return cst('ago_hours', { count: hours });
  return cst('ago_days', { count: Math.floor(hours / 24) });
}

// ---------------------------------------------------------------------------
// Confirmations (per browser — the tick is the installer's own note to self)
// ---------------------------------------------------------------------------

export const confirmationStorageKey = (agentId: string): string =>
  `mss.camera-setup.confirmed.${agentId}`;

/** A minimal Storage shape so tests can pass a plain object. */
export interface ConfirmationStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export function readConfirmations(
  store: ConfirmationStore | null | undefined,
  agentId: string,
): Confirmations {
  try {
    const raw = store?.getItem(confirmationStorageKey(agentId));
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    const out: Confirmations = {};
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isInteger(value)) out[key] = value;
    }
    return out;
  } catch {
    return {};
  }
}

export function writeConfirmations(
  store: ConfirmationStore | null | undefined,
  agentId: string,
  confirmations: Confirmations,
): void {
  try {
    store?.setItem(confirmationStorageKey(agentId), JSON.stringify(confirmations));
  } catch {
    // Private mode / blocked storage: the tick still lives in React state for
    // this visit, which is all the activation gate needs.
  }
}
